import React, { useEffect, useState, useCallback } from "react";
import LoadingInComponent from "../../Components/LoadingInComponent";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CONFIRM_IMPORT_API,
  FETCH_TEMP_GUEST_LIST_API,
} from "../../Constant/api";
import Button from "../../Components/Button";
import { GUEST_LIST } from "../../Router/path";
import GuestUploadPreviewTable from "../../Components/Tables/GuestUploadPreviewTable";
import { useToast } from "../../Components/Toast";
import { Warning2 } from "iconsax-react";
import Breadcrumb from "../../Components/Breadcrumb";
import { motion as m } from "framer-motion";

const GuestBulkUploadPreview = () => {
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [guestList, setGuestList] = useState();
  const [guestListMeta, setGuestListMeta] = useState({});
  const [missingRows, setMissingRows] = useState(null);
  const [rowWithIssues, setRowWithIssues] = useState(null);
  const [rowErrors, setRowErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { toast } = useToast();
  const { state } = useLocation();
  const navigate = useNavigate();

  const filteredGuestList = (guestList || []).filter((guests) => {
    const guestFirstNameMatch = guests.first_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const guestLastNameMatch = guests.last_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const groupMatch = guests.group_name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const emailMatch = guests.email
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const contactMatch =
      typeof guests.contact === "string"
        ? guests.contact.toLowerCase().includes(searchTerm.toLowerCase())
        : false;

    return (
      guestFirstNameMatch ||
      guestLastNameMatch ||
      contactMatch ||
      emailMatch ||
      groupMatch
    );
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const fetchTempGuestList = useCallback(async (page = 1, perPageValue = perPage) => {
    setLoading(true);
    try {
      axios
        .get(FETCH_TEMP_GUEST_LIST_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
            url: state.url,
            page: page,
            perPage: perPageValue,
            eventId: sessionStorage.getItem("event_id"),
          },
        })
        .then((res) => {
          setLoading(false);
          setGuestList(res.data.data.data);
          setGuestListMeta(res.data.data.pagination);
          setMissingRows(
            res.data.data.missing > 0 ? res.data.data.missing : undefined,
          );
          setRowWithIssues(res.data.data.rowWithIssue);
          setRowErrors(res.data.data.row_errors || []);
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          toast(err.response.data.data, "error");
        });
    } catch (error) {
      setLoading(false);
      toast("Failed to fetch data", "error");
      console.log(error);
    }
  }, [perPage, state.url, toast]);

  const handleConfirmImport = () => {
    setLoading(true);
    try {
      axios
        .post(
          CONFIRM_IMPORT_API,
          { filePath: state.url, eventId: sessionStorage.getItem("event_id") },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
          },
        )
        .then((res) => {
          setLoading(false);
          toast("Guest list imported successfully", "success");
          navigate(GUEST_LIST);
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
          toast("Failed to import", "error");
        });
    } catch (error) {
      setLoading(false);
      console.log(error);
      toast("Failed to import", "error");
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    fetchTempGuestList(1, e.target.value);
  };

  const handlePageClick = (e) => {
    fetchTempGuestList(e.selected + 1);
  };

  useEffect(() => {
    fetchTempGuestList();
  }, [fetchTempGuestList]);

  const breadcrumbItems = [
    { label: "Guest List", to: "/guest-list" },
    { label: "Import Guest List" },
    { label: "Preview" },
  ];

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <LoadingInComponent isLoading={loading}>
        {!loading && (
          <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[12px] md:gap-[24px]">
            {/* Breadcrumb */}
            <Breadcrumb items={breadcrumbItems} />

            {/* Title row */}
            <div className="flex flex-col md:flex-row w-full justify-between items-center">
              <h1 className="text-[29px]">Import Guest List Preview</h1>

              <div className="flex gap-[8px]">
                <Button onClick={handleConfirmImport}>Confirm Import</Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate(GUEST_LIST)}
                >
                  Cancel
                </Button>
              </div>
            </div>

            {/* Warnings */}
            {((missingRows && missingRows > 0) || (rowErrors && rowErrors.length > 0)) && (
                <m.div
                  className="flex w-full px-[24px] py-[16px] gap-4 border border-[#EFF0F2] rounded-xl items-center bg-white"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Warning2 size="24" variant="Outline" className="text-[#F0A009]" />
                  <div className="flex flex-col gap-1">
                    <p className="font-normal">
                      <span className="font-medium text-[#F0A009]">{`${missingRows || rowErrors.length} row(s) `}</span>
                      could not be imported due to validation errors.
                    </p>
                    {rowErrors.length > 0 && (
                      <div className="flex flex-col gap-1 text-sm text-gray-500 mt-1 max-h-[150px] overflow-y-auto">
                        {rowErrors.map((error, idx) => (
                          <p key={idx}>• {error}</p>
                        ))}
                      </div>
                    )}
                    {rowWithIssues && rowWithIssues.length > 0 && rowErrors.length === 0 && (
                      <p className="text-sm text-gray-500">Rows with issues: {rowWithIssues.join(", ")}</p>
                    )}
                  </div>
                </m.div>
              )}

            {/* Guest list table */}
            <div className="w-full">
              <GuestUploadPreviewTable
                searchable
                data={filteredGuestList}
                searchTerm={searchTerm}
                handleSearch={handleSearch}
                meta={guestListMeta}
                handlePageClick={handlePageClick}
                perPage={perPage}
                handlePerPageChange={handlePerPageChange}
              />
            </div>
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default GuestBulkUploadPreview;
